import React, { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LogIn, Mail, Lock, Award, Calendar, TrendingUp, UserPlus, Zap,
  MessageCircle, Star, X
} from 'lucide-react';

interface SidebarNote {
  id: string;
  icon: string;
  heading: string;
  description: string;
}
import { toast } from 'react-toastify';
import axios from '../utils/axios';
import { ErrorMessage } from '../components/ErrorMessage';

interface LoginFormData {
  identifier: string;
  password: string;
}

interface PortalSettings {
  whatsapp_registration_link: string;
  login_sidebar_notes: string;
  volunteer_of_month_name: string;
  volunteer_of_month_photo: string;
  volunteer_of_month_note: string;
  volunteer_of_month_period: string;
  popup_brochure_enabled: string;
  popup_brochure_image_url: string;
  popup_brochure_title: string;
}

const MemberLogin: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginFormData>({ identifier: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<LoginFormData>>({});
  const [portalSettings, setPortalSettings] = useState<PortalSettings | null>(null);
  const [sidebarNotes, setSidebarNotes] = useState<SidebarNote[]>([]);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get('/settings/public');
        const s: PortalSettings = {
          whatsapp_registration_link: res.data.settings?.whatsapp_registration_link || '',
          login_sidebar_notes: res.data.settings?.login_sidebar_notes || '',
          volunteer_of_month_name: res.data.settings?.volunteer_of_month_name || '',
          volunteer_of_month_photo: res.data.settings?.volunteer_of_month_photo || '',
          volunteer_of_month_note: res.data.settings?.volunteer_of_month_note || '',
          volunteer_of_month_period: res.data.settings?.volunteer_of_month_period || '',
          popup_brochure_enabled: res.data.settings?.popup_brochure_enabled || 'false',
          popup_brochure_image_url: res.data.settings?.popup_brochure_image_url || '',
          popup_brochure_title: res.data.settings?.popup_brochure_title || '',
        };
        setPortalSettings(s);
        // Parse notes JSON
        try {
          const parsed = JSON.parse(s.login_sidebar_notes || '[]');
          setSidebarNotes(Array.isArray(parsed) ? parsed : []);
        } catch {
          setSidebarNotes([]);
        }
        if (s.popup_brochure_enabled === 'true' && s.popup_brochure_image_url) {
          // Small delay so the page renders first
          setTimeout(() => setShowPopup(true), 600);
        }
      } catch {
        // silently fail — settings are non-critical
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.identifier.trim()) {
      setErrors(prev => ({ ...prev, identifier: 'Email or Member ID is required' }));
      return;
    }
    if (!formData.password) {
      setErrors(prev => ({ ...prev, password: 'Password is required' }));
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post('/auth/member/login', {
        email: formData.identifier.trim(),
        password: formData.password
      });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('userType', 'member');
      toast.success('Welcome back!');
      navigate('/member/dashboard');
    } catch (error: any) {
      if (error.response?.data?.errors) {
        const serverErrors = error.response.data.errors;
        serverErrors.forEach((err: any) => {
          if (err.path) setErrors(prev => ({ ...prev, [err.path]: err.msg }));
        });
        toast.error('Please fix the errors in the form');
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const dismissPopup = () => {
    setShowPopup(false);
  };

  const hasVolunteer = portalSettings?.volunteer_of_month_name;
  const hasNotes = sidebarNotes.length > 0;
  const hasWhatsApp = portalSettings?.whatsapp_registration_link;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex">
      {/* ── Popup Brochure ── */}
      {showPopup && portalSettings?.popup_brochure_image_url && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}
          onClick={dismissPopup}
        >
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={dismissPopup}
              className="absolute top-3 right-3 z-10 bg-black/40 hover:bg-black/60 text-white rounded-full p-1.5 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            {portalSettings.popup_brochure_title && (
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="font-bold text-[#0D5128] text-lg">
                  {portalSettings.popup_brochure_title}
                </h3>
              </div>
            )}
            <img
              src={portalSettings.popup_brochure_image_url}
              alt={portalSettings.popup_brochure_title || 'Brochure'}
              className="w-full max-h-[70vh] object-contain"
            />
            <div className="px-5 py-3 flex justify-end border-t border-gray-100">
              <button
                onClick={dismissPopup}
                className="text-sm text-gray-500 hover:text-gray-700 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Left Side — Login Form ── */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="bg-[#0D5128] p-3 rounded-2xl">
                <img
                  src="/website-04.png"
                  alt="Hammad Foundation"
                  className="w-100 h-auto object-contain"
                />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-[#0D5128]">Welcome Back</h2>
            <p className="mt-2 text-[#166534]">Sign in to access your volunteer portal</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-[#C49A3C]/20">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="identifier" className="block text-sm font-medium text-[#0D5128] mb-2">
                  Email or Member ID
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="identifier"
                    name="identifier"
                    type="text"
                    value={formData.identifier}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                      errors.identifier ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-[#0D5128]'
                    }`}
                    placeholder="15501 or your@email.com"
                  />
                </div>
                <ErrorMessage message={errors.identifier} />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="block text-sm font-medium text-[#0D5128]">
                    Password
                  </label>
                  <Link to="/forgot-password" className="text-xs text-[#C49A3C] hover:text-[#B8903A] font-medium">
                    Forgot Password?
                  </Link>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                      errors.password ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-[#0D5128]'
                    }`}
                    placeholder="••••••••"
                  />
                </div>
                <ErrorMessage message={errors.password} />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#0D5128] hover:bg-green-800 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <><LogIn className="h-5 w-5" />Sign In</>
                )}
              </button>
            </form>

            <div className="mt-6 space-y-3">
              <div className="text-center">
                <Link to="/register" className="text-sm text-[#0D5128] hover:text-green-800 font-medium">
                  Don't have an account? <span className="text-[#C49A3C]">Register here</span>
                </Link>
              </div>
              <div className="text-center">
                <Link to="/resend-verification" className="text-sm text-gray-600 hover:text-gray-900">
                  Resend verification email
                </Link>
              </div>
              <div className="text-center">
                <Link to="/" className="text-sm text-gray-600 hover:text-gray-900">
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right Side — Information Panel ── */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#0D5128] to-[#166534] p-12 items-center justify-center relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#C49A3C]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="relative z-10 max-w-lg w-full space-y-5">
          <div className="mb-2">
            <h2 className="text-4xl font-bold text-white mb-3">Join Our Volunteer Network</h2>
            <p className="text-white/80 text-lg">
              Make a difference in your community. Track your impact, earn achievements, and connect with fellow volunteers.
            </p>
          </div>

          {/* ── Dynamic Notes ── */}
          {hasNotes && (
            <div className="space-y-2">
              {sidebarNotes.map((note) => (
                <div
                  key={note.id}
                  className="flex items-start gap-3 bg-[#C49A3C]/15 backdrop-blur-sm rounded-xl p-4 border border-[#C49A3C]/30"
                >
                  <span className="text-xl shrink-0 leading-none mt-0.5">{note.icon}</span>
                  <div className="min-w-0">
                    {note.heading && (
                      <p className="text-white font-semibold text-sm mb-0.5">{note.heading}</p>
                    )}
                    {note.description && (
                      <p className="text-white/80 text-sm leading-relaxed">{note.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Volunteer of the Month ── */}
          {hasVolunteer && (
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-5 h-5 text-[#C49A3C] fill-[#C49A3C]" />
                <h3 className="text-white font-bold text-sm uppercase tracking-wide">
                  Volunteer of the Month
                </h3>
              </div>
              <div className="flex items-center gap-4">
                {portalSettings!.volunteer_of_month_photo ? (
                  <img
                    src={portalSettings!.volunteer_of_month_photo}
                    alt={portalSettings!.volunteer_of_month_name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-[#C49A3C]"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#C49A3C]/30 border-2 border-[#C49A3C] flex items-center justify-center">
                    <span className="text-white font-bold text-xl">
                      {portalSettings!.volunteer_of_month_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-white font-semibold">{portalSettings!.volunteer_of_month_name}</p>
                  {portalSettings!.volunteer_of_month_period && (
                    <p className="text-[#C49A3C] text-xs font-medium">{portalSettings!.volunteer_of_month_period}</p>
                  )}
                  {portalSettings!.volunteer_of_month_note && (
                    <p className="text-white/70 text-xs mt-0.5 leading-snug">
                      {portalSettings!.volunteer_of_month_note}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Feature Cards ── */}
          <div className="space-y-3">
            <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="bg-[#C49A3C] p-2.5 rounded-lg shrink-0">
                <UserPlus className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-0.5">Easy Registration</h3>
                <p className="text-white/70 text-sm">Quick and simple sign-up process. Get approved and start making an impact.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="bg-[#C49A3C] p-2.5 rounded-lg shrink-0">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-0.5">Event Participation</h3>
                <p className="text-white/70 text-sm">Browse and register for upcoming events. Scan QR codes for easy check-in.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="bg-[#C49A3C] p-2.5 rounded-lg shrink-0">
                <Award className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-0.5">Earn Points & Levels</h3>
                <p className="text-white/70 text-sm">Progress from Bronze to Platinum. Earn certificates and download your member card.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="bg-[#C49A3C] p-2.5 rounded-lg shrink-0">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-0.5">Track Your Impact</h3>
                <p className="text-white/70 text-sm">View your stats, rank among volunteers, and see your contribution history.</p>
              </div>
            </div>
          </div>

          {/* ── How to Get Started ── */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
            <h3 className="text-white font-bold text-base mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4 text-[#C49A3C]" />
              How to Get Started
            </h3>
            <ol className="space-y-2">
              {[
                'Create your account with valid credentials',
                'Wait for admin approval (usually within 24 hours)',
                'Login and complete your profile information',
                'Start attending events and making a difference!',
              ].map((step, i) => (
                <li key={i} className="flex items-center gap-3 text-white/90">
                  <span className="bg-[#C49A3C] w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                  <span className="text-sm">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* ── CTA Buttons ── */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/register"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#C49A3C] hover:bg-[#B8903A] text-white px-6 py-3 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl no-underline"
            >
              <UserPlus className="w-5 h-5" />
              Create Account
            </Link>
            {hasWhatsApp && (
              <a
                href={portalSettings!.whatsapp_registration_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-white/15 hover:bg-white/25 text-white border border-white/30 px-6 py-3 rounded-lg font-semibold transition-all no-underline"
              >
                <MessageCircle className="w-5 h-5" />
                Join WhatsApp
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberLogin;
